import { Phone, MapPin, Clock } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-zinc-950 border-t border-zinc-800" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <a 
              href="#hero" 
              onClick={(e) => { e.preventDefault(); scrollToSection('#hero'); }}
              className="inline-block mb-6"
            >
              <img 
                src="/logo.png" 
                alt="MV Auto Expert Logo" 
                className="h-20 w-auto"
                data-testid="footer-logo"
              />
            </a>
            <p className="text-zinc-400 mb-6 max-w-md">
              Especialistas en mecánica automotriz con más de 15 años de experiencia. 
              Servicio de calidad para vehículos domésticos e importados.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://wa.me/18628327369" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center hover:border-green-500 hover:bg-green-500/20 transition-colors"
                data-testid="footer-whatsapp"
              >
                <svg className="w-5 h-5 text-zinc-400 hover:text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
              <a 
                href="tel:+18628327369"
                className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center hover:border-blue-500 hover:bg-blue-500/20 transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5 text-zinc-400" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="text-lg font-bold uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Enlaces Rápidos
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'Inicio', href: '#hero' },
                { name: 'Servicios', href: '#services' },
                { name: 'Nosotros', href: '#about' },
                { name: 'Galería', href: '#gallery' },
                { name: 'Contacto', href: '#contact' },
                { name: 'Agendar Cita', href: '#appointments' },
              ].map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                    className="text-zinc-400 hover:text-yellow-400 transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 
              className="text-lg font-bold uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Información
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="text-yellow-400 flex-shrink-0 mt-1" size={18} />
                <span className="text-zinc-400">
                  280 W Clinton St.<br />Dover, NJ 07801
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="text-yellow-400 flex-shrink-0 mt-1" size={18} />
                <a href="tel:+18628327369" className="text-zinc-400 hover:text-yellow-400 transition-colors">
                  +1 (862) 832-7369
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="text-yellow-400 flex-shrink-0 mt-1" size={18} />
                <span className="text-zinc-400">
                  Lun - Vie: 8AM - 6PM<br />
                  Sábado: 9AM - 3PM
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-zinc-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm">
            © {currentYear} MV Auto Expert. Todos los derechos reservados.
          </p>
          <p className="text-zinc-600 text-sm">
            Especialistas en vehículos domésticos e importados
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
